/**
 * The connectivity state (PLAN §10.2) and the pure decisions over it: when an
 * endpoint counts as down or stably up, when to probe next, and which
 * endpoint is the primary backend. The manager (`manager.ts`) holds one of
 * these and feeds it probe results; everything here is a function of its
 * inputs, so the failover rules are unit-tested with a fake clock.
 */
import type { CloudLinkState } from '@acp/api-contract/health.js';
import type { StatusResponse } from '@acp/api-contract/statusRoutes.js';
import type { Endpoint } from './endpoints.js';

/** `unknown` until the first probe answers (or fails twice). */
export type Reach = 'unknown' | 'up' | 'down';

/** The primary's WebSocket, as the view holding it reports (`reportSocket`); `unknown` while nothing subscribes. */
export type SocketState = 'unknown' | 'connecting' | 'open' | 'closed';

export type NodeReport = { readonly nodeId: string; readonly cloudLink: CloudLinkState; readonly outboxDepth: number };

export type EndpointStatus = {
  readonly endpoint: Endpoint;
  readonly reach: Reach;
  /** The last probe's round trip (ms), answered or not; null before any. */
  readonly latencyMs: number | null;
  readonly version: string | null;
  readonly stage: string | null;
  readonly checkedAtMs: number | null;
  readonly lastOkAtMs: number | null;
  /** Consecutive failed probes (0 after a success). */
  readonly failures: number;
  /** Consecutive successful probes (0 after a failure). */
  readonly successes: number;
  /** Why the last probe failed, for the panel; null after a success. */
  readonly error: string | null;
  /** What a node says about itself: its site link and outbox. Null for the cloud, or before a node answered. */
  readonly node: NodeReport | null;
};

export type ConnectivityState = {
  readonly endpoints: readonly EndpointStatus[];
  /** Who served the page: decides the default preference (PLAN §10.2 endpoint selection). */
  readonly pageKind: 'cloud' | 'node';
  /** The primary backend's endpoint id: the data path. */
  readonly primaryId: string;
  /** The operator's explicit choice ("Switch to local"), or null for the default order. */
  readonly chosenId: string | null;
  readonly socket: SocketState;
  /** The cloud's view of the tenant's sites (`GET /status`), when it could be read. */
  readonly cloudView: StatusResponse | null;
};

/** Two failures in a row mark an endpoint down: one lost probe on a busy LAN is not an outage. */
export const DOWN_AFTER_FAILURES = 2;
/** Two successes in a row before the SPA moves its primary BACK to a preferred endpoint: no flapping. */
export const STABLE_AFTER_SUCCESSES = 2;
/** How often a healthy endpoint is probed. */
export const HEALTHY_INTERVAL_MS = 10_000;
/** Probing a failing endpoint backs off from this… */
export const RETRY_BASE_MS = 1_000;
/** …to this. Short enough that a node back from a restart is noticed within seconds. */
export const RETRY_MAX_MS = 15_000;

export function initialStatus(endpoint: Endpoint): EndpointStatus {
  return { endpoint, reach: 'unknown', latencyMs: null, version: null, stage: null, checkedAtMs: null, lastOkAtMs: null, failures: 0, successes: 0, error: null, node: null };
}

/** When to probe an endpoint again, after `failures` consecutive failures (0: it is healthy). */
export function nextProbeDelayMs(failures: number): number {
  if (failures <= 0) return HEALTHY_INTERVAL_MS;
  return Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (failures - 1));
}

export type ProbeOutcome =
  | { readonly ok: true; readonly latencyMs: number; readonly version: string; readonly stage: string; readonly node: NodeReport | null }
  | { readonly ok: false; readonly latencyMs: number; readonly error: string };

/** An endpoint after one probe at `nowMs`. */
export function applyProbe(status: EndpointStatus, outcome: ProbeOutcome, nowMs: number): EndpointStatus {
  if (outcome.ok) {
    return {
      ...status,
      reach: 'up',
      latencyMs: outcome.latencyMs,
      version: outcome.version,
      stage: outcome.stage,
      checkedAtMs: nowMs,
      lastOkAtMs: nowMs,
      failures: 0,
      successes: status.successes + 1,
      error: null,
      node: outcome.node,
    };
  }
  const failures = status.failures + 1;
  return {
    ...status,
    reach: failures >= DOWN_AFTER_FAILURES ? 'down' : status.reach,
    latencyMs: outcome.latencyMs,
    checkedAtMs: nowMs,
    failures,
    successes: 0,
    error: outcome.error,
  };
}

/**
 * A data request to this endpoint just failed at the network level. It is
 * SUSPECT: no longer `up` (so nothing resends to it before a probe says so),
 * and one failure short of down, so the probe the manager runs at once decides
 * on its own — a failure there makes it down, a success makes it up again.
 */
export function applyRequestFailure(status: EndpointStatus): EndpointStatus {
  return { ...status, reach: status.reach === 'down' ? 'down' : 'unknown', failures: Math.max(status.failures, DOWN_AFTER_FAILURES - 1), successes: 0 };
}

/**
 * The order in which endpoints are preferred as the primary (only those that `canBePrimary`):
 *   - the operator's choice first, when there is one;
 *   - a page served by a node: the nodes (as listed, the serving one first), then the cloud;
 *   - a page served by the cloud: the cloud, then the nodes — the SPA OFFERS the switch to
 *     local rather than making it (PLAN §10.2), but still fails over to a node if the cloud drops.
 */
export function preferenceOrder(state: Pick<ConnectivityState, 'endpoints' | 'pageKind' | 'chosenId'>): string[] {
  const ids = (kind: 'cloud' | 'node') => state.endpoints.filter((s) => s.endpoint.kind === kind && s.endpoint.canBePrimary).map((s) => s.endpoint.id);
  const base = state.pageKind === 'node' ? [...ids('node'), ...ids('cloud')] : [...ids('cloud'), ...ids('node')];
  const chosen = state.chosenId;
  return chosen !== null && base.includes(chosen) ? [chosen, ...base.filter((id) => id !== chosen)] : base;
}

/**
 * The primary backend, given the current one:
 *   1. a more preferred endpoint that is STABLY up (STABLE_AFTER_SUCCESSES) wins — moving back
 *      to it after a failover, or to the operator's choice;
 *   2. otherwise the current primary stays, unless it is down;
 *   3. a down primary gives way to the most preferred endpoint that is up;
 *   4. with nothing up, the current primary stays (there is nothing better to switch to).
 * `unknown` is not down: at start-up the page's own backend is the primary until proven otherwise.
 */
export function choosePrimary(state: Pick<ConnectivityState, 'endpoints' | 'pageKind' | 'chosenId' | 'primaryId'>): string {
  const order = preferenceOrder(state);
  const byId = new Map(state.endpoints.map((s) => [s.endpoint.id, s]));
  const current = byId.get(state.primaryId);
  const currentRank = order.indexOf(state.primaryId);
  for (const id of order.slice(0, currentRank === -1 ? order.length : currentRank)) {
    const s = byId.get(id);
    if (s !== undefined && s.reach === 'up' && s.successes >= STABLE_AFTER_SUCCESSES) return id;
  }
  if (current !== undefined && current.reach !== 'down') return state.primaryId;
  for (const id of order) if (byId.get(id)?.reach === 'up') return id;
  return state.primaryId;
}

/**
 * "Switch to local" (PLAN §10.2): offered on a cloud-served page whose primary
 * is the cloud, when a node is up — the id of the first such node, else null.
 */
export function localOffer(state: Pick<ConnectivityState, 'endpoints' | 'pageKind' | 'primaryId'>): string | null {
  if (state.pageKind !== 'cloud') return null;
  const primary = state.endpoints.find((s) => s.endpoint.id === state.primaryId);
  if (primary?.endpoint.kind !== 'cloud') return null;
  return state.endpoints.find((s) => s.endpoint.kind === 'node' && s.endpoint.canBePrimary && s.reach === 'up')?.endpoint.id ?? null;
}
